import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import DateTimePicker from "components/DateTimePicker";
import {
  getProposalContext,
  getProposalPreview,
  saveProposal,
  sendProposalToClient,
  generateProposalDocument,
} from "api/discussion";
import { getWorkflowStatus } from "api/applications";
import { toast } from "sonner";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { toLocalYmd } from "../../utils/filingApiDates";
import type {
  ProposalContextResponse,
  ProposalPreviewResponse,
  SaveProposalPayload,
} from "api/discussion";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { HiOutlineX } from "react-icons/hi";
import ConfirmMessageModal from "components/ConfirmMessageModal";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function formatProposalRupees(n: number): string {
  return `Rs.${Math.round(n).toLocaleString("en-IN")}/-`;
}

function ProposalPreviewRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 text-[13px] leading-relaxed text-[#222222] md:grid-cols-[200px_12px_1fr] md:items-baseline">
      <span className="font-semibold text-[#101111]">{label}</span>
      <span className="hidden font-semibold text-[#101111] md:inline">:</span>
      <span className="break-words">{value}</span>
    </div>
  );
}

/** On-screen layout aligned with source doc page 35 (same fields as generated proposal PDF). */
function ProposalPage35Preview({ data }: { data: ProposalPreviewResponse }) {
  const nonDistYesNo = data.nonDistinctiveness ? "YES" : "NO";
  const typesLabel =
    data.typesOfMarks.length > 0 ? data.typesOfMarks.join(", ") : "—";
  const addresseeValue = [
    data.addressee.designation,
    data.addressee.firmName,
    data.addressee.place,
  ]
    .filter((v) => v && v.trim().length > 0)
    .join(", ") || "—";

  return (
    <div className="mx-auto max-w-2xl px-1 py-2 font-inter">
      <p className="mb-3 border-b border-[#EEEEEE] pb-2 text-sm font-semibold text-[#101111]">
        Data : Proposal for New filing
      </p>
      <div className="flex flex-col gap-y-1.5">
        <ProposalPreviewRow label="Addressee" value={addresseeValue} />
        <ProposalPreviewRow
          label="Form of Addressee"
          value={data.addressee.honorific || "—"}
        />
        <ProposalPreviewRow label="Mark" value={data.markName || "—"} />
        <div className="grid grid-cols-1 gap-x-3 gap-y-0.5 text-[13px] leading-relaxed text-[#222222] md:grid-cols-[200px_12px_1fr] md:items-baseline">
          <span className="font-semibold text-[#101111]">
            Class &amp; Goods / Services
          </span>
          <span className="hidden font-semibold text-[#101111] md:inline">:</span>
          <div className="flex flex-col gap-0.5">
            {data.classes.length === 0 ? (
              <span className="text-[#6D6D6D]">—</span>
            ) : (
              data.classes.map((c) => (
                <p key={c.classNumber} className="break-words">
                  <span className="font-semibold text-[#101111]">
                    Class {c.classNumber} :{" "}
                  </span>
                  <span>{c.goodServices || "—"}</span>
                  <span className="font-semibold text-[#101111]"> | Similarity : </span>
                  <span>{c.similarity ? "YES" : "NO"}</span>
                </p>
              ))
            )}
          </div>
        </div>
        <ProposalPreviewRow label="Non distinctiveness" value={nonDistYesNo} />
        <ProposalPreviewRow
          label="Govt Fee for New filing"
          value={formatProposalRupees(data.govtFee)}
        />
        <ProposalPreviewRow
          label="Discount in Prof Fee for New filing"
          value={formatProposalRupees(data.profFeeDiscount)}
        />
        <ProposalPreviewRow
          label="Prof Fee for New filing"
          value={formatProposalRupees(data.profFee)}
        />
        <ProposalPreviewRow
          label="Total Charges"
          value={formatProposalRupees(data.totalCharges)}
        />
        <ProposalPreviewRow label="Selection of Types of Marks" value={typesLabel} />
        <ProposalPreviewRow
          label="Proposal No. for New Filing"
          value={data.proposalNo ?? "—"}
        />
        <ProposalPreviewRow
          label="Date of Proposal for New Filing"
          value={data.proposalDate ?? "—"}
        />
      </div>
    </div>
  );
}

function ProposalPreviewTabsPanel({
  data,
  pdfUrl,
  previewTab,
  setPreviewTab,
  numPages,
  setNumPages,
  pdfMaxWidth = 800,
  embedInDialog = true,
}: {
  data: ProposalPreviewResponse;
  pdfUrl: string | null;
  previewTab: "summary" | "pdf";
  setPreviewTab: Dispatch<SetStateAction<"summary" | "pdf">>;
  numPages: number | null;
  setNumPages: (n: number | null) => void;
  /** Max width for PDF page rendering (narrower inside confirm dialog). */
  pdfMaxWidth?: number;
  /** When true, cap scroll height for use inside ConfirmMessageModal. */
  embedInDialog?: boolean;
}) {
  const pageWidth = Math.min(
    pdfMaxWidth,
    typeof window !== "undefined" ? window.innerWidth - 48 : pdfMaxWidth
  );

  return (
    <div
      className={`flex min-h-0 flex-col ${embedInDialog ? "" : "h-full min-h-0"}`}
    >
      <div
        className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "summary"}
          onClick={() => setPreviewTab("summary")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "summary"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Proposal details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "pdf"}
          onClick={() => setPreviewTab("pdf")}
          disabled={!pdfUrl}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
            previewTab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF
        </button>
      </div>
      <div
        className={
          embedInDialog
            ? "max-h-[55vh] min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-3 md:max-h-[min(480px,55vh)]"
            : "min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4"
        }
      >
        {previewTab === "summary" ? (
          <div className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
            <ProposalPage35Preview data={data} />
          </div>
        ) : pdfUrl ? (
          <Document
            file={pdfUrl}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<p className="text-sm text-[#6d6d6d]">Loading PDF…</p>}
            error={<p className="text-sm text-red-600">Failed to load PDF.</p>}
            className="flex flex-col items-center gap-4"
          >
            {numPages != null &&
              Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderTextLayer
                  renderAnnotationLayer
                />
              ))}
          </Document>
        ) : (
          <p className="text-sm text-[#6d6d6d]">No PDF URL available.</p>
        )}
      </div>
    </div>
  );
}

const KERALA_DISTRICTS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
] as const;

type ProposalToClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function ProposalToClientView({
  leadId,
  onSuccess,
  onSavingChange,
  onSentStatusChange,
}: ProposalToClientViewProps) {
  const [loading, setLoading] = useState(!!leadId);
  const [context, setContext] = useState<ProposalContextResponse | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [proposalPreviewData, setProposalPreviewData] =
    useState<ProposalPreviewResponse | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<"summary" | "pdf">("summary");
  const [previewing, setPreviewing] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [whatsappConfirmOpen, setWhatsappConfirmOpen] = useState(false);
  const [whatsappConfirmMode, setWhatsappConfirmMode] = useState<"send" | "resend">("send");

  // Form state - only fields that exist in backend
  const [addresseeDesignation, setAddresseeDesignation] = useState("");
  const [addresseeCustomName, setAddresseeCustomName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [place, setPlace] = useState("");
  const [addresseeHonorific, setAddresseeHonorific] = useState("");
  const [govtFee, setGovtFee] = useState("");
  const [discountInProfFee, setDiscountInProfFee] = useState("");
  const [typesOfMarks, setTypesOfMarks] = useState<string[]>([]);
  const [proposalDate, setProposalDate] = useState<Date | null>(null);
  const [isManualPlace, setIsManualPlace] = useState(false);

  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [resending, setResending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setContext(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getProposalContext(leadId)
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        const sp = ctx.savedProposal;
        setAddresseeDesignation(sp.addresseeDesignation ?? ctx.addresseeOptions[0] ?? "");
        setAddresseeCustomName(sp.addresseeCustomName ?? "");
        setFirmName(sp.firmName ?? "");
        const savedPlace = sp.place ?? "";
        const defaultDistrict = KERALA_DISTRICTS[0];
        setPlace(savedPlace || defaultDistrict);
        setIsManualPlace(
          !!savedPlace && !KERALA_DISTRICTS.some((d) => d.toLowerCase() === savedPlace.toLowerCase())
        );
        setAddresseeHonorific(sp.addresseeHonorific ?? ctx.addresseeHonorificOptions[0] ?? "Sir");
        const gfo = ctx.govtFeeOptions?.length ? ctx.govtFeeOptions : [4500, 9000];
        setGovtFee(sp.govtFee != null ? String(sp.govtFee) : (gfo[0] ? String(gfo[0]) : "4500"));
        setDiscountInProfFee(sp.profFeeDiscount ? String(sp.profFeeDiscount) : "");
        setTypesOfMarks(
          Array.isArray(sp.typesOfMarks) && sp.typesOfMarks.length > 0
            ? sp.typesOfMarks
            : ["Word"]
        );
        if (ctx.proposalDate) {
          setProposalDate(new Date(ctx.proposalDate));
        } else {
          setProposalDate(new Date());
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load proposal");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getWorkflowStatus(leadId);
        // "Resend to WhatsApp" must depend on the step that indicates WhatsApp was sent.
        // In backend status, the WhatsApp-sent marker appears as `S1_09C_PROPOSAL_SENT_TO_CLIENT`.
        // (The parent step `S1_09_PROPOSAL_TO_CLIENT` may still be incomplete.)
        const step = status.steps.find(
          (s) => s.slug === "S1_09C_PROPOSAL_SENT_TO_CLIENT"
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  const addresseeOptions = context?.addresseeOptions ?? [];
  const addresseeHonorificOptions = context?.addresseeHonorificOptions ?? ["Sir", "Madam"];
  const keralaDistricts = [...KERALA_DISTRICTS];
  const govtFeeOptions = (context?.govtFeeOptions?.length ? context.govtFeeOptions : [4500, 9000]) as number[];
  const availableTypesOfMarks = ["Word", "Label", "Logo", "Slogan"];

  const isCustomAddressee = addresseeDesignation === "NAME";

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!addresseeDesignation) errs.addresseeDesignation = "Addressee is required";
    if (isCustomAddressee && !addresseeCustomName.trim()) errs.addresseeCustomName = "Custom addressee name is required";
    if (!firmName.trim()) errs.firmName = "Firm name is required";
    if (!place.trim()) errs.place = "Place is required";
    if (!addresseeHonorific) errs.addresseeHonorific = "Honorific is required";
    if (!govtFee) errs.govtFee = "Govt fee is required";
    if (typesOfMarks.length === 0) errs.typesOfMarks = "Select at least one type of mark";
    if (!proposalDate) errs.proposalDate = "Date of proposal is required";
    return errs;
  };

  const buildSavePayload = (): SaveProposalPayload => ({
    addresseeDesignation: addresseeDesignation || addresseeOptions[0],
    firmName: firmName || "",
    place: place || keralaDistricts[0] || "",
    addresseeHonorific: addresseeHonorific || addresseeHonorificOptions[0],
    govtFee: (parseFloat(govtFee) || govtFeeOptions[0]) ?? 4500,
    profFeeDiscount: parseFloat(discountInProfFee) || 0,
    typesOfMarks: typesOfMarks.length > 0 ? typesOfMarks : undefined,
    proposalDate: proposalDate ? toLocalYmd(proposalDate) : undefined,
    ...(isCustomAddressee ? { addresseeCustomName } : {}),
  });

  const prepareProposalForWhatsAppConfirm = async (): Promise<boolean> => {
    if (!leadId || !context) return false;
    try {
      await saveProposal(leadId, buildSavePayload());
      const [preview, { downloadUrl }] = await Promise.all([
        getProposalPreview(leadId),
        generateProposalDocument(leadId),
      ]);
      setProposalPreviewData(preview);
      setPreviewPdfUrl(downloadUrl);
      setPreviewTab("summary");
      setNumPages(null);
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to prepare proposal for sending"
      );
      return false;
    }
  };

  const handleConfirmWhatsAppSend = () => {
    if (!leadId) return;
    setSaving(true);
    onSavingChange?.(true);
    void (async () => {
      try {
        await sendProposalToClient(leadId);
        setHasSentWhatsapp(true);
        onSentStatusChange?.(true);
        const isResend = whatsappConfirmMode === "resend";
        toast.success(
          isResend
            ? "Proposal resent to client successfully."
            : "Proposal saved and sent to client successfully."
        );
        setSuccessMessage(
          isResend
            ? "Proposal resent to client successfully."
            : "Proposal saved and sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to send proposal to client"
        );
      } finally {
        setSaving(false);
        onSavingChange?.(false);
      }
    })();
  };

  const closeWhatsAppConfirm = () => {
    setWhatsappConfirmOpen(false);
    setProposalPreviewData(null);
    setPreviewPdfUrl(null);
    setNumPages(null);
    setPreviewTab("summary");
  };

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !context || saving) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fill all required fields");
      return;
    }

    if (hasSentWhatsapp) {
      onSavingChange?.(true);
      setSuccessMessage(null);
      setSaving(true);
      try {
        const payload: SaveProposalPayload = {
          addresseeDesignation,
          firmName,
          place,
          addresseeHonorific,
          govtFee: (parseFloat(govtFee) || govtFeeOptions[0]) ?? 4500,
          profFeeDiscount: parseFloat(discountInProfFee) || 0,
          typesOfMarks: typesOfMarks.length > 0 ? typesOfMarks : undefined,
          proposalDate: proposalDate ? toLocalYmd(proposalDate) : undefined,
        };
        if (isCustomAddressee) {
          payload.addresseeCustomName = addresseeCustomName || undefined;
        }
        await saveProposal(leadId, payload);
        toast.success("Proposal saved successfully.");
        setSuccessMessage("Proposal saved successfully.");
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to save proposal");
      } finally {
        setSaving(false);
        onSavingChange?.(false);
      }
      return;
    }

    setWhatsappConfirmOpen(false);
    setPreviewModalOpen(false);
    setSaving(true);
    onSavingChange?.(true);
    setSuccessMessage(null);
    try {
      const ok = await prepareProposalForWhatsAppConfirm();
      if (ok) {
        setWhatsappConfirmMode("send");
        setWhatsappConfirmOpen(true);
      }
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId) return;
    setGenerating(true);
    try {
      await saveProposal(leadId, buildSavePayload());
      await generateProposalDocument(leadId);
      toast.success("Proposal PDF generated successfully.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!leadId || !context) return;
    setGenerating(true);
    try {
      await saveProposal(leadId, buildSavePayload());
      const { downloadUrl, filename } = await generateProposalDocument(leadId);
      const result = await downloadFileFromUrlWithFallback(
        downloadUrl,
        filename ?? "proposal.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download");
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId || !context) return;
    setWhatsappConfirmOpen(false);
    setPreviewPdfUrl(null);
    setProposalPreviewData(null);
    setPreviewTab("summary");
    setNumPages(null);
    setPreviewModalOpen(false);
    setPreviewing(true);
    try {
      await saveProposal(leadId, buildSavePayload());
      const [preview, { downloadUrl }] = await Promise.all([
        getProposalPreview(leadId),
        generateProposalDocument(leadId),
      ]);
      setProposalPreviewData(preview);
      setPreviewPdfUrl(downloadUrl);
      setPreviewModalOpen(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewPdfUrl(null);
    setProposalPreviewData(null);
    setNumPages(null);
    setPreviewTab("summary");
  };

  if (!leadId) {
    return (
      <div className="flex w-full max-w-[1085px] flex-col gap-5 font-inter">
        <p className="text-xs text-[#6D6D6D]">
          Please complete the previous steps to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex w-full max-w-[1085px] items-center justify-center py-12">
        <p className="text-sm text-[#6D6D6D]">Loading proposal...</p>
      </div>
    );
  }

  if (!context) return null;

  const markName = context.markName ?? "";
  const nonDistinctivenessText =
    context.nonDistinctiveness === true
      ? "Non-Distinctive"
      : context.nonDistinctiveness === false
        ? "Distinct"
        : "Not Available";

  return (
    <form
      id="proposal-form"
      onSubmit={handleSaveAndContinue}
      className="flex w-full max-w-[1085px] flex-col gap-5 font-inter"
    >
      {successMessage && (
        <div className="text-xs text-green-600">
          {successMessage}
        </div>
      )}

      {/* Addressee Designation */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-normal leading-5 text-[#222222]">
          Addressee <span className="text-red-500">*</span>
        </label>
        <Select
          value={addresseeDesignation || undefined}
          onValueChange={(v) => { setAddresseeDesignation(v); setErrors((p) => { const n = {...p}; delete n.addresseeDesignation; return n; }); }}
        >
          <SelectTrigger className={`h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px] ${errors.addresseeDesignation ? "border-red-400 ring-1 ring-red-400" : ""}`}>
            <SelectValue placeholder="Select addressee" />
          </SelectTrigger>
          <SelectContent>
            {addresseeOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.addresseeDesignation && <p className="text-xs text-red-500 mt-0.5">{errors.addresseeDesignation}</p>}
      </div>

      {isCustomAddressee && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Custom Addressee Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={addresseeCustomName}
            onChange={(e) => { setAddresseeCustomName(e.target.value); setErrors((p) => { const n = {...p}; delete n.addresseeCustomName; return n; }); }}
            placeholder="Text Here"
            className={`h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border bg-white px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 ${errors.addresseeCustomName ? "border-red-400 focus:ring-red-400" : "border-[#EEEEEE] focus:border-[#222222] focus:ring-[#222222]"}`}
          />
          {errors.addresseeCustomName && <p className="text-xs text-red-500 mt-0.5">{errors.addresseeCustomName}</p>}
        </div>
      )}

      {/* Firm Name | Place */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Firm Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={firmName}
            onChange={(e) => { setFirmName(e.target.value); setErrors((p) => { const n = {...p}; delete n.firmName; return n; }); }}
            placeholder="Text Here"
            className={`h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border bg-white px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 ${errors.firmName ? "border-red-400 focus:ring-red-400" : "border-[#EEEEEE] focus:border-[#222222] focus:ring-[#222222]"}`}
          />
          {errors.firmName && <p className="text-xs text-red-500 mt-0.5">{errors.firmName}</p>}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Place <span className="text-red-500">*</span>
          </label>
          {!isManualPlace ? (
            <Select
              value={place || undefined}
              onValueChange={(v) => {
                setPlace(v);
                setIsManualPlace(false);
                setErrors((p) => { const n = {...p}; delete n.place; return n; });
              }}
            >
              <SelectTrigger className={`h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px] ${errors.place ? "border-red-400 ring-1 ring-red-400" : ""}`}>
                <SelectValue placeholder="Select district" />
              </SelectTrigger>
              <SelectContent>
                {keralaDistricts.map((district) => (
                  <SelectItem key={district} value={district}>
                    {district}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <input
              type="text"
              value={place}
              onChange={(e) => { setPlace(e.target.value); setErrors((p) => { const n = {...p}; delete n.place; return n; }); }}
              placeholder="Enter place manually"
              className={`h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border bg-white px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 ${errors.place ? "border-red-400 focus:ring-red-400" : "border-[#EEEEEE] focus:border-[#222222] focus:ring-[#222222]"}`}
            />
          )}
          {errors.place && <p className="text-xs text-red-500 mt-0.5">{errors.place}</p>}
          <button
            type="button"
            onClick={() => {
              setIsManualPlace((prev) => {
                const next = !prev;
                if (!next && !keralaDistricts.includes(place as (typeof KERALA_DISTRICTS)[number])) {
                  setPlace(keralaDistricts[0] ?? "");
                }
                return next;
              });
            }}
            className="self-start text-[11px] md:text-xs text-[#222222] underline underline-offset-2"
          >
            {isManualPlace ? "Use district dropdown" : "Enter place manually"}
          </button>
        </div>
      </div>

      {/* Addressee Honorific */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-normal leading-5 text-[#222222]">
          Honorific <span className="text-red-500">*</span>
        </label>
        <Select
          value={addresseeHonorific || undefined}
          onValueChange={(v) => { setAddresseeHonorific(v); setErrors((p) => { const n = {...p}; delete n.addresseeHonorific; return n; }); }}
        >
          <SelectTrigger className={`h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm rounded-[6px] ${errors.addresseeHonorific ? "border-red-400 ring-1 ring-red-400" : ""}`}>
            <SelectValue placeholder="Select honorific" />
          </SelectTrigger>
          <SelectContent>
            {addresseeHonorificOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.addresseeHonorific && <p className="text-xs text-red-500 mt-0.5">{errors.addresseeHonorific}</p>}
      </div>

      {/* Govt Fee | Discount in Prof Fee */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Govt Fee for New filing <span className="text-red-500">*</span>
          </label>
          <Select
            value={govtFee || undefined}
            onValueChange={(v) => { setGovtFee(v); setErrors((p) => { const n = {...p}; delete n.govtFee; return n; }); }}
          >
            <SelectTrigger className={`h-9 min-h-[44px] md:min-h-0 w-full text-[12px] md:text-sm ${errors.govtFee ? "border-red-400 ring-1 ring-red-400" : ""}`}>
              <SelectValue placeholder="Select govt fee" />
            </SelectTrigger>
            <SelectContent>
              {govtFeeOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.govtFee && <p className="text-xs text-red-500 mt-0.5">{errors.govtFee}</p>}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Discount in Prof Fee
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={discountInProfFee}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d.]/g, "");
              const parts = v.split(".");
              const sanitized =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : v;
              setDiscountInProfFee(sanitized);
            }}
            placeholder="00.00"
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:border-[#222222] focus:outline-none focus:ring-1 focus:ring-[#222222]"
          />
        </div>
      </div>

      {/* Types of Marks - multi-select (API supports string[]) */}
      {availableTypesOfMarks.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-normal leading-5 text-[#222222]">
            Types of Marks <span className="text-red-500">*</span>
          </span>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {availableTypesOfMarks.map((opt) => (
              <label
                key={opt}
                htmlFor={`types-of-marks-${opt}`}
                className="flex cursor-pointer items-center gap-2 text-xs font-normal leading-5 text-[#222222] min-h-[44px] md:min-h-0 touch-manipulation"
              >
                <Checkbox
                  id={`types-of-marks-${opt}`}
                  checked={typesOfMarks.includes(opt)}
                  onCheckedChange={(checked) => {
                    setTypesOfMarks((prev) => {
                      const next = checked ? (prev.includes(opt) ? prev : [...prev, opt]) : prev.filter((v) => v !== opt);
                      if (next.length > 0) setErrors((p) => { const n = {...p}; delete n.typesOfMarks; return n; });
                      return next;
                    });
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
          {errors.typesOfMarks && <p className="text-xs text-red-500 mt-0.5">{errors.typesOfMarks}</p>}
        </div>
      )}

      {/* Mark - readonly from API */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-normal leading-5 text-[#222222]">
          Mark
        </label>
        <input
          type="text"
          value={markName}
          readOnly
          className="h-9 min-h-[44px] md:min-h-0 w-full max-w-full md:max-w-[532px] rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] md:text-xs capitalize leading-5 text-[#222222] cursor-not-allowed"
        />
      </div>

      {/* Classes - row per class: Class | Goods / Services | Similarity; mobile: stacked cards */}
      <div className="flex flex-col gap-4">
        <div className="hidden md:flex gap-5 items-center text-sm font-medium text-[#222222]">
          <span className="w-[170px] shrink-0">Class</span>
          <span className="flex-1 min-w-0">Goods / Services</span>
          <span className="w-[260px] shrink-0">Similarity</span>
        </div>
        <div className="flex flex-col gap-3">
          {context.classes.map((c) => (
            <div
              key={c.classNumber}
              className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 p-4 md:p-0 rounded-[6px] border border-[#EEE] md:border-0 bg-white md:bg-transparent"
            >
              <div className="flex flex-col gap-1 md:contents">
                <span className="md:hidden text-[12px] font-medium text-[#222222]">Class</span>
                <div className="w-full md:w-[170px] md:shrink-0 rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2.5 flex items-center min-h-[44px] md:min-h-0">
                  <span className="text-[12px] md:text-sm leading-5 text-[#222222]">
                    Class {c.classNumber}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1 md:contents flex-1 min-w-0">
                <span className="md:hidden text-[12px] font-medium text-[#222222]">Goods / Services</span>
                <div className="w-full md:flex-1 min-w-0 rounded-[6px] border border-[#EEEEEE] bg-white p-2.5 flex flex-wrap gap-2 items-center min-h-[44px] md:min-h-0">
                  {Array.isArray(c.goodsServices) && c.goodsServices.length > 0 ? (
                    c.goodsServices.map((gs, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center justify-center rounded-[6px] bg-[#F7F7F7] px-1 py-0.5 text-[12px] md:text-sm leading-5 text-[#222222]"
                      >
                        {gs}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-[#6D6D6D]">—</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1 md:contents">
                <span className="md:hidden text-[12px] font-medium text-[#222222]">Similarity</span>
                <div className="w-full md:w-[260px] md:shrink-0 rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2.5 flex items-center min-h-[44px] md:min-h-0">
                  <span className="text-[12px] md:text-sm leading-5 text-[#222222]">
                    {c.similarity ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Non distinctiveness | Proposal No. - readonly */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Non-Distinctiveness
          </label>
          <input
            type="text"
            value={nonDistinctivenessText}
            readOnly
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] md:text-xs leading-5 text-[#222222] cursor-not-allowed"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label className="text-xs font-normal leading-5 text-[#222222]">
            Proposal No. for New Filing
          </label>
          <input
            type="text"
            value={context.proposalNo ?? ""}
            readOnly
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] md:text-xs capitalize leading-5 text-[#222222] cursor-not-allowed"
          />
        </div>
      </div>

      {/* Date of Proposal - DateTimePicker from preliminary-discussion */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-normal leading-5 text-[#222222]">
          Date of Proposal for New Filing <span className="text-red-500">*</span>
        </label>
        <DateTimePicker
          value={proposalDate}
          onChange={(d) => { setProposalDate(d); if (d) setErrors((p) => { const n = {...p}; delete n.proposalDate; return n; }); }}
          buttonClassName={`min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs ${errors.proposalDate ? "!border-red-400 !ring-1 !ring-red-400" : ""}`}
        />
        {errors.proposalDate && <p className="text-xs text-red-500 mt-0.5">{errors.proposalDate}</p>}
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <button
          type="button"
          onClick={handleGeneratePdf}
          disabled={generating}
          className="flex min-h-[44px] h-auto py-2.5 sm:h-9 sm:py-2 items-center justify-center gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-4 text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 touch-manipulation"
        >
          <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
          {generating ? "Generating..." : "Generate PDF"}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={generating}
          className="min-h-[44px] sm:h-9 rounded-[6px] bg-[#222222] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-white hover:bg-[#333333] disabled:opacity-60 touch-manipulation"
        >
          Download
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="min-h-[44px] sm:h-9 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 touch-manipulation"
        >
          {previewing ? "Generating..." : "Preview"}
        </button>
      </div>

      <ConfirmMessageModal
        open={whatsappConfirmOpen && !!proposalPreviewData}
        onClose={closeWhatsAppConfirm}
        title="Confirm proposal to client"
        previewSlot={
          proposalPreviewData ? (
            <ProposalPreviewTabsPanel
              data={proposalPreviewData}
              pdfUrl={previewPdfUrl}
              previewTab={previewTab}
              setPreviewTab={setPreviewTab}
              numPages={numPages}
              setNumPages={setNumPages}
              pdfMaxWidth={640}
            />
          ) : null
        }
        onConfirm={handleConfirmWhatsAppSend}
        disableConfirm={saving}
      />

      {/* Proposal preview (Preview button only): same tabs as WhatsApp confirm, without send. */}
      {previewModalOpen && proposalPreviewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm">
                Proposal preview
              </span>
              <button
                type="button"
                onClick={closePreviewModal}
                className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-2 sm:p-4">
            <ProposalPreviewTabsPanel
              data={proposalPreviewData}
              pdfUrl={previewPdfUrl}
              previewTab={previewTab}
              setPreviewTab={setPreviewTab}
              numPages={numPages}
              setNumPages={setNumPages}
              pdfMaxWidth={800}
              embedInDialog={false}
            />
            </div>
          </div>
        </div>
      )}

      {hasSentWhatsapp && leadId && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={async () => {
              if (!leadId || !context || resending) return;
              const errs = validate();
              setErrors(errs);
              if (Object.keys(errs).length > 0) {
                toast.error("Please fill all required fields");
                return;
              }
              setPreviewModalOpen(false);
              setResending(true);
              onSavingChange?.(true);
              try {
                const ok = await prepareProposalForWhatsAppConfirm();
                if (ok) {
                  setWhatsappConfirmMode("resend");
                  setWhatsappConfirmOpen(true);
                }
              } finally {
                setResending(false);
                onSavingChange?.(false);
              }
            }}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Preparing…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}
